import { COUNTRY } from 'common/constants';
import { FormValidator } from 'common/Validator/Validator';

import { INSURANCE_FORM_VALIDATOR_CONFIG, PATIENT_INFO_VALIDATOR_CONFIG } from './Insurance/form-validator-configs';

import { SURVEY_VALIDATOR_CONFIG } from './Survey/form-validator-config';
import { CheckoutStepDefinition, CheckoutStepParams, StepId } from './types';

const isCanadianClinic = (clinic: any) => clinic?.address?.country === COUNTRY.canada;

/**
 * The ordered list of steps in the checkout process.
 * This should not be used directly to determine the current step.
 * Instead, use the `useCurrentCheckoutStep` hook to get the current step.
 */
export const stepDefinitions: CheckoutStepDefinition[] = [
  {
    id: StepId.ACCOUNT,
    name: 'Info',
    path: 'account',
    showInProgress: true,
    isApplicable: () => true,
    isComplete: ({ isUserAuthenticated }: CheckoutStepParams) => {
      return isUserAuthenticated;
    },
  },
  { // Used if the user doesn't have a phone number associated with their account
    id: StepId.PHONE_NUMBER,
    name: 'Phone Number',
    path: 'phone-number',
    showInProgress: false,
    isApplicable: ({ patient }: CheckoutStepParams) => !patient?.mobilePhone,
    isComplete: ({ patient }: CheckoutStepParams) => Boolean(patient?.mobilePhone),
  },
  {
    id: StepId.ONGOING_CARE,
    name: 'Ongoing Care',
    path: 'ongoing-care',
    showInProgress: false,
    isApplicable: ({ clinic, match }: CheckoutStepParams) => {
      if (!isCanadianClinic(clinic) || match?.didSkipFunnel) {
        return false;
      }

      return !match?.other?.ongoingCare;
    },
    isComplete: ({ match, checkoutOngoingCareAnswer }: CheckoutStepParams) => {
      return Boolean(match?.other?.ongoingCare || checkoutOngoingCareAnswer);
    },
  },
  {
    id: StepId.TREATMENTS,
    name: 'Treatments',
    path: 'treatments',
    showInProgress: false,
    isApplicable: ({ clinic, match }: CheckoutStepParams) => {
      if (!isCanadianClinic(clinic) || match?.didSkipFunnel) {
        return false;
      }

      return !match?.other?.selectedTreatments?.length;
    },
    isComplete: ({ match, checkoutSelectedTreatments }: CheckoutStepParams) => {
      return Boolean(match?.other?.selectedTreatments?.length || checkoutSelectedTreatments?.length);
    },
  },
  {
    id: StepId.PATIENT_INFO,
    name: 'Insurance',
    path: 'patient',
    reduxKey: 'patient',
    showInProgress: true,
    isApplicable: () => true,
    isComplete: ({ checkoutState }: CheckoutStepParams) => {
      const { firstName, lastName, dateOfBirth, sexAssignedAtBirth } = checkoutState.patient;
      const { day, month, year } = dateOfBirth;
      const flattenedFields = { firstName, lastName, sexAssignedAtBirth, day, month, year };

      const patientFormValidator = new FormValidator(PATIENT_INFO_VALIDATOR_CONFIG);
      const validationResult = patientFormValidator.validate(flattenedFields, { showReturningPatientToggle: false });
      return validationResult.allValid;
    },
  },
  {
    id: StepId.SURVEY,
    name: 'Survey',
    path: 'survey',
    reduxKey: 'survey',
    showInProgress: false,
    // Show if the funnel was skipped
    isApplicable: ({ match }: CheckoutStepParams) => match?.didSkipFunnel,
    isComplete: ({ checkoutState }: CheckoutStepParams) => {
      const { survey } = checkoutState;

      if (!survey) {
        return false;
      }

      const surveyFormValidator = new FormValidator(SURVEY_VALIDATOR_CONFIG);
      const validationResult = surveyFormValidator.validate(
        survey,
        { urgent: survey.urgent },
      );
      return validationResult.allValid;
    },
  },
  {
    id: StepId.INSURANCE,
    name: 'Insurance',
    path: 'insurance',
    reduxKey: 'insurance',
    showInProgress: false,
    isApplicable: ({ match }: CheckoutStepParams) => match?.other?.insurance?.hasInsurance !== 'No',
    isComplete: ({ checkoutState, clinic, match, isMemberIdOptional }: CheckoutStepParams) => {
      const isUsClinic = !isCanadianClinic(clinic);
      const insuranceFormValidator = new FormValidator(INSURANCE_FORM_VALIDATOR_CONFIG);

      const { hasInsurance, provider, plan, memberId, isPrimaryMember, primaryMember } = checkoutState.insurance;

      const insuranceFields = {
        hasInsurance,
        insuranceProvider: provider,
        insurancePlan: plan,
        memberId,
        isPrimaryMember,
        primaryMemberFirstName: primaryMember?.firstName,
        primaryMemberLastName: primaryMember?.lastName,
        primaryMemberBirthYear: primaryMember?.dateOfBirth?.year,
        primaryMemberBirthMonth: primaryMember?.dateOfBirth?.month,
        primaryMemberBirthDay: primaryMember?.dateOfBirth?.day,
      };

      const showHasInsuranceQuestion = match?.other?.hasInsurance === null;

      const params = {
        isUsClinic,
        isPrimaryMember: Boolean(isPrimaryMember),
        showHasInsuranceQuestion,
        hasSelectedNoInsurance: !hasInsurance,
        isMemberIdOptional,
      };
      const insuranceValidationResult = insuranceFormValidator.validate(insuranceFields, params);
      return insuranceValidationResult.allValid;
    },
  },
  {
    id: StepId.VERIFY,
    name: 'Verify',
    path: 'verify',
    reduxKey: 'verify',
    showInProgress: false,
    isApplicable: () => true,
    isComplete: ({ patient }: CheckoutStepParams) =>
      Boolean(patient.emailVerifiedAt && (patient.phoneVerifiedAt && !patient.unverifiedPhoneData)),
  },
  {
    id: StepId.CONFIRM,
    name: 'Confirm',
    path: 'confirm',
    reduxKey: 'confirm',
    showInProgress: true,
    isApplicable: () => true,
    isComplete: () => false,
  },
];
